import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconAlertTriangle, IconArrowLeft, IconEye, IconTrash } from '@tabler/icons-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UPLOAD_IMAGE_MIME_TYPES } from '@creativitas/shared';
import type { AccessRule, TrainingBlockType } from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { AssetImage } from '@/components/ui/AssetImage';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { ADMIN_CONTENT_KEY, fetchAdminContent } from './api';
import { AccessRulesEditor } from './AccessRulesEditor';
import { BlockBuilder } from './BlockBuilder';
import {
  ADMIN_TRAINING_KEY,
  addBlock,
  deleteBlock,
  deleteMaterial,
  fetchAdminMaterial,
  reorderBlocks,
  setAccessRules,
  updateBlock,
  updateMaterial,
  uploadImage,
} from './content-api';

/** Material editor: blocks, order, access and publication (§45, §48). */
export function AdminTrainingEditorPage() {
  const { materialId = '' } = useParams();
  /* «?tab=access» — прямой переход к доступу из списка. */
  const [searchParams] = useSearchParams();
  const initialTab = ['blocks', 'access', 'settings'].includes(searchParams.get('tab') ?? '')
    ? searchParams.get('tab')!
    : 'blocks';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const materialKey = [...ADMIN_TRAINING_KEY, materialId];
  const query = useQuery({
    queryKey: materialKey,
    queryFn: () => fetchAdminMaterial(materialId),
  });
  const contentQuery = useQuery({ queryKey: ADMIN_CONTENT_KEY, queryFn: fetchAdminContent });

  const [title, setTitle] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);

  const material = query.data;
  const games = (contentQuery.data?.items ?? []).filter((item) => item.kind === 'game' && item.variant !== 'demo');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: materialKey });
    void queryClient.invalidateQueries({ queryKey: ADMIN_TRAINING_KEY });
    void queryClient.invalidateQueries({ queryKey: ['training'] });
  };

  const fail = (heading: string) => (error: unknown) =>
    notifications.show({ title: heading, message: toUserMessage(error), color: 'red' });

  const updateMutation = useMutation({
    mutationFn: (input: Parameters<typeof updateMaterial>[1]) => updateMaterial(materialId, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(materialKey, updated);
      invalidate();
      notifications.show({ message: 'Материал обновлён', color: 'teal' });
    },
    onError: fail('Не удалось сохранить материал'),
  });

  const addBlockMutation = useMutation({
    mutationFn: (type: TrainingBlockType) => addBlock(materialId, { type }),
    onSuccess: (updated) => {
      queryClient.setQueryData(materialKey, updated);
      invalidate();
    },
    onError: fail('Не удалось добавить блок'),
  });

  const saveBlockMutation = useMutation({
    mutationFn: ({
      blockId,
      patch,
    }: {
      blockId: string;
      patch: { title: string | null; body: string | null; url: string | null };
    }) => updateBlock(blockId, patch),
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Блок сохранён', color: 'teal' });
    },
    onError: fail('Не удалось сохранить блок'),
  });

  const deleteBlockMutation = useMutation({
    mutationFn: deleteBlock,
    onSuccess: () => invalidate(),
    onError: fail('Не удалось удалить блок'),
  });

  const reorderMutation = useMutation({
    mutationFn: (blockIds: string[]) => reorderBlocks(materialId, blockIds),
    onSuccess: (updated) => {
      queryClient.setQueryData(materialKey, updated);
      invalidate();
    },
    onError: fail('Не удалось изменить порядок'),
  });

  const coverMutation = useMutation({
    mutationFn: async (file: File) => {
      const stored = await uploadImage(file);
      return updateMaterial(materialId, { coverImage: stored.url });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(materialKey, updated);
      invalidate();
    },
    onError: fail('Не удалось загрузить обложку'),
  });

  const accessMutation = useMutation({
    mutationFn: ({ rules, gameId }: { rules: AccessRule[]; gameId: string | null }) =>
      setAccessRules('training_material', materialId, rules, gameId),
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Правила доступа сохранены', color: 'teal' });
    },
    onError: fail('Не удалось сохранить доступ'),
  });

  const deleteMaterialMutation = useMutation({
    mutationFn: () => deleteMaterial(materialId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_TRAINING_KEY });
      notifications.show({ message: 'Материал удалён', color: 'teal' });
      void navigate('/admin/training');
    },
    onError: fail('Не удалось удалить материал'),
  });

  if (query.isLoading) return <LoadingState label="Открываем материал..." />;
  if (query.isError || !material) return <ErrorState onRetry={() => void query.refetch()} />;

  const published = material.status === 'published';
  const busy = saveBlockMutation.isPending || addBlockMutation.isPending || reorderMutation.isPending;

  return (
    <>
      <PageHeader
        eyebrow="Администрирование · обучение"
        title={material.title}
        description={`${material.blockCount} блоков`}
        actions={
          <Group gap="sm">
            <Badge size="lg" variant={published ? 'light' : 'default'} color={published ? 'teal' : 'gray'}>
              {published ? 'Опубликован' : 'Черновик'}
            </Badge>
            <Button
              component={Link}
              to={`/training/${material.slug}`}
              variant="light"
              leftSection={<IconEye size={15} />}
            >
              Как видит пользователь
            </Button>
            <Button
              component={Link}
              to="/admin/training"
              variant="default"
              leftSection={<IconArrowLeft size={15} />}
            >
              К списку
            </Button>
          </Group>
        }
      />

      <Tabs defaultValue={initialTab}>
        <Tabs.List>
          <Tabs.Tab value="blocks">Блоки</Tabs.Tab>
          <Tabs.Tab value="access">Доступ</Tabs.Tab>
          <Tabs.Tab value="settings">Настройки</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="blocks" pt="lg">
          <Stack gap="md" maw={760}>
            <BlockBuilder
              blocks={material.blocks}
              saving={busy}
              onAdd={(type) => addBlockMutation.mutate(type)}
              onSave={(blockId, patch) => saveBlockMutation.mutate({ blockId, patch })}
              onDelete={(blockId) => deleteBlockMutation.mutate(blockId)}
              onReorder={(blockIds) => reorderMutation.mutate(blockIds)}
            />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="access" pt="lg">
          <Paper withBorder p="lg" radius="lg" maw={640} style={{ borderColor: 'var(--border-subtle)' }}>
            <Stack gap="md">
              <Stack gap={2}>
                <Title order={4}>Кому доступен материал</Title>
                <Text size="sm" c="var(--text-muted)">
                  Достаточно выполнить любое одно правило.
                </Text>
              </Stack>
              <AccessRulesEditor
                value={material.accessRules}
                requiredGameId={material.accessRequiredGameId}
                games={games}
                saving={accessMutation.isPending}
                onSave={(rules, gameId) => accessMutation.mutate({ rules, gameId })}
              />
            </Stack>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="settings" pt="lg">
          <Stack gap="lg" maw={640}>
            <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
              <Stack gap="md">
                <Title order={4}>Основное</Title>
                <TextInput
                  label="Название"
                  value={title ?? material.title}
                  onChange={(event) => setTitle(event.currentTarget.value)}
                />
                <Textarea
                  label="Описание"
                  minRows={3}
                  value={description ?? material.description ?? ''}
                  onChange={(event) => setDescription(event.currentTarget.value)}
                />
                <Group>
                  <Button
                    size="sm"
                    loading={updateMutation.isPending}
                    disabled={title === null && description === null}
                    onClick={() =>
                      updateMutation.mutate({
                        ...(title !== null ? { title } : {}),
                        ...(description !== null ? { description } : {}),
                      })
                    }
                  >
                    Сохранить
                  </Button>
                </Group>

                <Stack gap="xs">
                  <Text size="sm" fw={600}>
                    Обложка
                  </Text>
                  <div style={{ maxWidth: 320 }}>
                    <AssetImage
                      src={material.coverImage}
                      alt=""
                      ratio="16 / 9"
                      fit="contain"
                      fallbackLabel="Не задана"
                    />
                  </div>
                  <Dropzone
                    onDrop={(files) => files[0] && coverMutation.mutate(files[0])}
                    accept={[...UPLOAD_IMAGE_MIME_TYPES]}
                    loading={coverMutation.isPending}
                    maxFiles={1}
                    p="xs"
                    radius="md"
                    maw={320}
                    style={{ borderColor: 'var(--border-subtle)' }}
                  >
                    <Text size="xs" ta="center" c="var(--text-muted)" style={{ pointerEvents: 'none' }}>
                      Загрузить обложку
                    </Text>
                  </Dropzone>
                </Stack>
              </Stack>
            </Paper>

            <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
              <Stack gap="md">
                <Title order={4}>Публикация</Title>

                {material.blocks.length === 0 ? (
                  <Alert variant="light" color="gold" icon={<IconAlertTriangle size={16} />}>
                    Нельзя опубликовать материал без блоков.
                  </Alert>
                ) : material.accessRules.length === 0 && !published ? (
                  <Alert variant="light" color="gold" icon={<IconAlertTriangle size={16} />}>
                    Правила доступа не заданы — материал увидит только администратор.
                  </Alert>
                ) : null}

                <Group>
                  <Button
                    variant={published ? 'default' : 'filled'}
                    loading={updateMutation.isPending}
                    disabled={!published && material.blocks.length === 0}
                    onClick={() =>
                      updateMutation.mutate({ status: published ? 'draft' : 'published' })
                    }
                  >
                    {published ? 'Снять с публикации' : 'Опубликовать'}
                  </Button>

                  <Button
                    variant="light"
                    color="red"
                    leftSection={<IconTrash size={15} />}
                    onClick={() =>
                      modals.openConfirmModal({
                        title: 'Удалить материал?',
                        children: (
                          <Text size="sm">
                            Материал «{material.title}» и его блоки будут удалены. Это действие
                            необратимо.
                          </Text>
                        ),
                        labels: { confirm: 'Удалить', cancel: 'Отмена' },
                        confirmProps: { color: 'red' },
                        onConfirm: () => deleteMaterialMutation.mutate(),
                      })
                    }
                  >
                    Удалить материал
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
